import React, { useState } from 'react';
import { ExclamationTriangleIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { FormSubmission } from '../../types/Form';

interface BulkDeleteModalProps {
  isOpen: boolean;
  selectedSubmissions: FormSubmission[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({
  isOpen,
  selectedSubmissions,
  onConfirm,
  onClose
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const count = selectedSubmissions.length;
  const requireConfirmation = count > 10;
  const isConfirmed = !requireConfirmation || confirmText.toUpperCase() === 'DELETE';

  // Calculate status breakdown
  const statusBreakdown = selectedSubmissions.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const hasAttached = selectedSubmissions.some(s => s.leadId !== null);
  const attachedCount = selectedSubmissions.filter(s => s.leadId !== null).length;

  const handleConfirm = async () => {
    if (!isConfirmed) return;
    
    setIsDeleting(true);
    try {
      await onConfirm();
      setConfirmText('');
      onClose();
    } catch (error) {
      console.error('Bulk delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setConfirmText('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Delete {count} {count === 1 ? 'Submission' : 'Submissions'}?
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>This action cannot be undone.</strong> You are about to permanently delete {count} form {count === 1 ? 'submission' : 'submissions'}.
            </p>
          </div>

          {/* Attached Warning */}
          {hasAttached && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 flex items-start">
                <ExclamationTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Warning:</strong> {attachedCount} {attachedCount === 1 ? 'submission is' : 'submissions are'} already attached to leads. 
                  Deleting these submissions will NOT delete the associated leads.
                </span>
              </p>
            </div>
          )}

          {/* Status Breakdown */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Submissions by Status:</h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(statusBreakdown).map(([status, count]) => (
                <div key={status} className="bg-gray-50 border border-gray-200 rounded px-3 py-2">
                  <span className="text-xs text-gray-600 capitalize">{status.replace('_', ' ')}: </span>
                  <span className="text-sm font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Confirmation Input for Large Deletions */}
          {requireConfirmation && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-red-600">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                disabled={isDeleting}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isConfirmed || isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <TrashIcon className="w-4 h-4" />
                <span>Delete {count} {count === 1 ? 'Submission' : 'Submissions'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default BulkDeleteModal;
